import type { User } from 'firebase/auth';
import { errorReporter } from '@/services/errorReportingService';
import { sendSessionToExtension } from './extensionCommunication';

// Timeout for extension response - increased for slower machines/high-load scenarios
const EXTENSION_RESPONSE_TIMEOUT_MS = 3000;

type ExtensionSyncReason = 'login' | 'page-load' | 'token-refresh';

type ExtensionResponse = {
  responded: boolean;
  responseType?: string;
};

type CreateSessionResponse = {
  sessionToken: string;
  expiresAt: string;
  user: {
    uid: string;
    email: string | null;
    userDocId: string | null;
  };
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitForExtensionResponse = (timeoutMs: number): Promise<ExtensionResponse> =>
  new Promise((resolve) => {
    let settled = false;

    const handler = (event: MessageEvent) => {
      // The extension content script posts on this same window — reject anything
      // from another window (frames, popups) or another origin.
      if (event.source !== window || event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as { source?: string; type?: string } | null;
      if (!data) return;

      const isExtensionSource = data.source === 'notego-extension';
      const isKnownType = data.type === 'EXTENSION_AUTH_STATE' || data.type === 'EXTENSION_PONG';

      if (!isExtensionSource && !isKnownType) {
        return;
      }

      if (settled) return;
      settled = true;
      window.removeEventListener('message', handler);
      window.clearTimeout(timeoutId);
      resolve({
        responded: true,
        responseType: typeof data.type === 'string' ? data.type : 'unknown',
      });
    };

    window.addEventListener('message', handler);

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', handler);
      resolve({ responded: false });
    }, timeoutMs);
  });

/**
 * Request a session token from the API for the Chrome extension.
 * Returns null if the request fails (non-critical - old extension versions don't need it).
 */
async function createExtensionSession(firebaseToken: string): Promise<CreateSessionResponse | null> {
  const baseUrl = import.meta.env.VITE_API_URL;
  if (!baseUrl) {
    errorReporter.debug('VITE_API_URL not set, skipping extension session creation', {
      feature: 'extension',
      operation: 'createExtensionSession',
    });
    return null;
  }

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await fetch(`${baseUrl}/api/extension/create-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (attempt === 0) {
          errorReporter.debug(`Extension session creation returned ${response.status}, retrying`, {
            feature: 'extension',
            operation: 'createExtensionSession',
          });
          await delay(2000);
          continue;
        }
        errorReporter.expected(`Extension session creation failed with status ${response.status}`, undefined, {
          feature: 'extension',
          operation: 'createExtensionSession',
        });
        return null;
      }

      const json = await response.json();
      const sessionToken = json?.sessionToken;
      const expiresAt = json?.expiresAt;
      const user = json?.user;
      const hasValidUser =
        user &&
        typeof user === 'object' &&
        typeof user.uid === 'string' &&
        'email' in user &&
        'userDocId' in user &&
        (user.email === null || typeof user.email === 'string') &&
        (user.userDocId === null || typeof user.userDocId === 'string');
      if (typeof sessionToken !== 'string' || typeof expiresAt !== 'string' || !hasValidUser) {
        errorReporter.expected('Extension session response missing or invalid required fields', undefined, {
          feature: 'extension',
          operation: 'createExtensionSession',
        });
        return null;
      }
      return json as CreateSessionResponse;
    } catch (error) {
      if (attempt === 0) {
        errorReporter.debug('Extension session creation failed, retrying', {
          feature: 'extension',
          operation: 'createExtensionSession',
        });
        await delay(2000);
        continue;
      }
      errorReporter.expected('Extension session creation failed after retry', error, {
        feature: 'extension',
        operation: 'createExtensionSession',
      });
      return null;
    }
  }
  return null;
}

// Debounce state for syncAuthToExtension — collapses rapid calls (login + page-load + token-refresh)
// into a single session creation + STORE_SESSION message. All callers' promises settle only after
// the actual sync attempt finishes.
let syncDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingResolvers: Array<() => void> = [];

export async function syncAuthToExtension(
  user: User,
  options: { reason: ExtensionSyncReason; token?: string },
): Promise<void> {
  // Debounce: if called multiple times within 200ms, only the last call runs.
  // All callers' promises are collected and resolved together after the sync completes.
  if (syncDebounceTimer) {
    clearTimeout(syncDebounceTimer);
  }

  return new Promise<void>((resolve) => {
    pendingResolvers.push(resolve);

    syncDebounceTimer = setTimeout(async () => {
      syncDebounceTimer = null;
      const resolvers = pendingResolvers;
      pendingResolvers = [];

      try {
        const { token: providedToken } = options;
        const token = providedToken ?? (await user.getIdToken(true));

        const responsePromise = waitForExtensionResponse(EXTENSION_RESPONSE_TIMEOUT_MS);

        window.postMessage({ type: 'PING_EXTENSION', source: 'notego-web' }, window.location.origin);
        window.postMessage({ type: 'REQUEST_EXTENSION_AUTH' }, window.location.origin);

        // Send legacy STORE_TOKEN for backwards compatibility with older extension versions
        window.postMessage(
          {
            type: 'STORE_TOKEN',
            token,
            user: {
              uid: user.uid,
              email: user.email ?? '',
              displayName: user.displayName ?? '',
              photoURL: user.photoURL ?? null,
              emailVerified: user.emailVerified ?? false,
            },
          },
          window.location.origin,
        );

        // Request a session token from the API and send it to the extension.
        // This is fire-and-forget: if it fails, the extension still has the Firebase token.
        createExtensionSession(token)
          .then((session) => {
            if (session) {
              sendSessionToExtension(session.sessionToken, session.expiresAt, session.user);
            }
          })
          .catch((error) => {
            errorReporter.expected('Extension session creation/delivery failed', error, {
              feature: 'extension',
              operation: 'createExtensionSession',
            });
          });

        await responsePromise;
      } catch (error) {
        errorReporter.expected('Extension sync failed', error, {
          feature: 'extension',
          operation: 'syncAuthToExtension',
        });
      } finally {
        resolvers.forEach((r) => r());
      }
    }, 200);
  });
}
